<script>
import CannedResponseAttachments from './CannedResponseAttachments.vue';

const MAX_LABEL = 20;
// With a button the message is sent as an interactive/button-template body:
// Messenger truncates past 640 chars and WhatsApp rejects past 1024 — 640
// covers both (same cap the bot pages enforce). Mirrors the model validation.
const MAX_BODY_WITH_BUTTON = 640;
// Same invisible-bidi strip the auto-reply bot page applies: pasted Arabic can
// carry marks that make an identical-looking label miss its match. Written as
// escapes on purpose: the characters themselves are invisible in source.
const cleanText = value =>
  String(value || '')
    .replace(/[\u200E\u200F\u202A-\u202E\u2066-\u2069\uFEFF]/g, '')
    .trim();

const INPUT_CLASS =
  'w-full rounded-lg border border-n-weak bg-n-alpha-black1 px-2.5 py-1.5 text-[13px] font-semibold text-n-slate-12 outline-none transition-colors placeholder:font-normal placeholder:text-n-slate-9 focus:border-n-teal-9';

export default {
  name: 'CannedButtonFields',
  components: { CannedResponseAttachments },
  props: {
    label: { type: String, default: '' },
    replyContent: { type: String, default: '' },
    attachments: { type: Array, default: () => [] },
    // The canned response's own message text, for the 640-char with-button cap.
    content: { type: String, default: '' },
    // The canned response being edited, so its own stored label never counts
    // as a duplicate of itself.
    excludeId: { type: Number, default: null },
  },
  emits: [
    'update:label',
    'update:replyContent',
    'update:attachments',
    'update:error',
  ],
  data() {
    return { MAX_LABEL, INPUT_CLASS };
  },
  computed: {
    cleanLabel() {
      return cleanText(this.label);
    },
    hasReply() {
      return Boolean(
        this.replyContent.trim() || (this.attachments || []).length
      );
    },
    // Every text the bot tree answers to: screen titles, their buttons, and the
    // greeting entry buttons, across all inboxes. A colliding label would be
    // hijacked by the bot at runtime.
    botButtonTexts() {
      const inboxes = this.$store.getters['inboxes/getInboxes'] || [];
      const texts = new Set();
      inboxes.forEach(inbox => {
        (inbox.greeting_buttons || []).forEach(b => texts.add(cleanText(b)));
        (inbox.button_flow || []).forEach(node => {
          texts.add(cleanText(node.title));
          (node.buttons || []).forEach(b => texts.add(cleanText(b)));
        });
      });
      texts.delete('');
      return texts;
    },
    duplicateLabel() {
      const records = this.$store.getters.getCannedResponses || [];
      return records.some(
        record =>
          record.id !== this.excludeId &&
          cleanText(record.button_label) === this.cleanLabel &&
          this.cleanLabel
      );
    },
    error() {
      if (!this.cleanLabel && !this.hasReply) return null;
      if (!this.cleanLabel)
        return this.$t('CANNED_MGMT.BUTTON.ERROR_NEED_LABEL');
      if (!this.hasReply) return this.$t('CANNED_MGMT.BUTTON.ERROR_NEED_REPLY');
      if (this.content.length > MAX_BODY_WITH_BUTTON)
        return this.$t('CANNED_MGMT.BUTTON.ERROR_BODY_TOO_LONG', {
          max: MAX_BODY_WITH_BUTTON,
          length: this.content.length,
        });
      if (this.duplicateLabel)
        return this.$t('CANNED_MGMT.BUTTON.ERROR_DUPLICATE');
      if (this.botButtonTexts.has(this.cleanLabel))
        return this.$t('CANNED_MGMT.BUTTON.ERROR_BOT_COLLISION');
      return null;
    },
  },
  watch: {
    error: {
      immediate: true,
      handler(value) {
        this.$emit('update:error', value);
      },
    },
  },
  mounted() {
    // The collision check needs the bot tree; inboxes may not be loaded yet on
    // a settings-page hard refresh.
    this.$store.dispatch('inboxes/get');
  },
};
</script>

<template>
  <div class="grid gap-3 sm:grid-cols-2">
    <div>
      <div class="mb-1 flex items-center justify-between">
        <label class="!mb-0 text-[12px] font-bold text-n-slate-12">
          {{ $t('CANNED_MGMT.BUTTON.LABEL') }}
        </label>
        <span
          class="text-[11px] font-bold tabular-nums"
          :class="
            label.length >= MAX_LABEL ? 'text-n-amber-11' : 'text-n-slate-10'
          "
          dir="ltr"
        >
          {{ `${label.length}/${MAX_LABEL}` }}
        </span>
      </div>
      <input
        :value="label"
        type="text"
        :maxlength="MAX_LABEL"
        :class="INPUT_CLASS"
        :placeholder="$t('CANNED_MGMT.BUTTON.LABEL_PLACEHOLDER')"
        @input="$emit('update:label', $event.target.value)"
      />
    </div>

    <div>
      <label class="mb-1 block text-[12px] font-bold text-n-slate-12">
        {{ $t('CANNED_MGMT.BUTTON.REPLY') }}
        <span class="ms-1 font-medium text-n-slate-10">
          {{ $t('CANNED_MGMT.BUTTON.REPLY_OPTIONAL') }}
        </span>
      </label>
      <textarea
        :value="replyContent"
        rows="2"
        :class="INPUT_CLASS"
        class="resize-y leading-relaxed"
        :placeholder="$t('CANNED_MGMT.BUTTON.REPLY_PLACEHOLDER')"
        @input="$emit('update:replyContent', $event.target.value)"
      />
    </div>

    <div class="sm:col-span-2">
      <label class="mb-1 block text-[12px] font-bold text-n-slate-12">
        {{ $t('CANNED_MGMT.BUTTON.REPLY_ATTACHMENT') }}
        <span class="ms-1 font-medium text-n-slate-10">
          {{ $t('CANNED_MGMT.BUTTON.REPLY_OPTIONAL') }}
        </span>
      </label>
      <CannedResponseAttachments
        :model-value="attachments"
        :max-attachments="1"
        :max-file-size-mb="16"
        accept="image/*,video/mp4,application/pdf"
        :add-label="$t('CANNED_MGMT.BUTTON.ATTACHMENT_ADD')"
        :hint="$t('CANNED_MGMT.BUTTON.ATTACHMENT_HINT')"
        @update:model-value="$emit('update:attachments', $event)"
      />
    </div>

    <p
      v-if="error"
      class="flex items-start gap-1.5 rounded-lg bg-n-ruby-3 px-2.5 py-1.5 text-[11.5px] font-bold leading-relaxed text-n-ruby-11 sm:col-span-2"
    >
      <span class="i-lucide-triangle-alert mt-0.5 size-3.5 shrink-0" />
      {{ error }}
    </p>
  </div>
</template>
